import { useState, useEffect, useMemo } from 'react';
import { Lead, LeadFilters } from '../types/LeadTypes';
import { filterLeadsByTimeFrame, TimeFrameOption, DEFAULT_TIMEFRAME, validateTimeFrame } from '../utils/dateFilters';
import { applyFiltersToLead } from '../utils/filterUtils';
import { useFilters } from '@/hooks/useFilters';

interface UseLeadFilteringProps {
  leads: Lead[];
  searchTerm?: string;
}

export const useLeadFiltering = ({ 
  leads, 
  searchTerm = ''
}: UseLeadFilteringProps) => {
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  
  const initialFilters: LeadFilters = {
    statuses: [],
    outcomes: [],
    minValue: '',
    maxValue: '',
    types: [],
    leadQuality: ['qualified', 'disqualified'],
    dateRange: {
      from: undefined,
      to: undefined,
    }
  };
  
  const { 
    filters, 
    setFilters, 
    hasActiveFilters: hasAnyActiveFilters 
  } = useFilters<LeadFilters>({ 
    initialFilters 
  });
  
  // Calculate if we have active filters, excluding the default leadQuality state
  const hasActiveFilters = useMemo(() => {
    // Check if leadQuality is in default state (both qualified and disqualified selected)
    const isDefaultQualityFilter = 
      filters.leadQuality.length === 2 && 
      filters.leadQuality.includes('qualified') && 
      filters.leadQuality.includes('disqualified');
    
    // Default leadQuality state should not trigger the active filters indicator
    if (isDefaultQualityFilter) {
      // Check other filters
      const hasOtherFilters = 
        filters.statuses.length > 0 || 
        filters.outcomes.length > 0 || 
        filters.types.length > 0 || 
        !!filters.minValue || 
        !!filters.maxValue || 
        !!filters.dateRange.from || 
        !!filters.dateRange.to;
      
      return hasOtherFilters;
    }
    
    // If leadQuality is not in default state, use the standard hasActiveFilters
    return hasAnyActiveFilters;
  }, [filters, hasAnyActiveFilters]);
  
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);
  
  // Remove timeFrame filtering
  // Only apply other filters
  const filteredLeads = useMemo(() => 
    leads.filter(lead => applyFiltersToLead(lead, filters, localSearchTerm)),
    [leads, filters, localSearchTerm]
  );

  return {
    localSearchTerm,
    setLocalSearchTerm,
    filters,
    setFilters,
    hasActiveFilters,
    filteredLeads
  };
};
